/**
 * Coinbase Derivatives — frontend service.
 *
 * Fetches Decision Lab payload, order history, and kill switch management.
 */

import api from './api';

// ─── Payload types (mirror of backend CoinbaseDecisionLabPayload) ─

export interface CoinbaseWsFeedHealth {
  connected: boolean;
  status: string;
  subscribedProducts: string[];
  activeChannels: string[];
  sequenceGap: boolean;
  reconnectCount: number;
  lastMessageAt?: string;
  lastHeartbeatAt?: string;
  reasons: string[];
}

export interface CoinbaseRiskCapitalView {
  capitalBaseUsd: number;
  activeTradingCapitalUsd: number;
  drawdownReserveUsd: number;
  lockedProfitUsd: number;
  maxRiskPerTradeUsd: number;
  maxDailyLossUsd: number;
  maxWeeklyLossUsd: number;
  marginBudgetUsd: number;
  status: 'ACTIVE' | 'PROTECTION' | 'DISABLED';
}

export interface CoinbaseCapitalEvent {
  id: string;
  type: string;
  at: string;
  amountUsd?: number;
  note?: string;
}

export interface CoinbaseJournalEntry {
  timestamp: string;
  mode: string;
  productId: string;
  side?: string;
  strategyClass?: string;
  freshnessVerdict?: string;
  finalAction: string;
  dominantBlocker?: string;
  dominantSizeCap?: string;
  apgeGrade?: string;
  apgeAction?: string;
  tomiScore?: number;
  kazeScore?: number;
  flowStatus?: string;
  approvedSize?: number;
  oneLine: string;
}

export interface CoinbaseDecisionLabPayload {
  timestamp: string;
  engine: {
    mode: string;
    phase: string;
    status: 'OBSERVE_ONLY' | 'PAPER' | 'LIVE' | 'PROTECTION' | 'SHUTDOWN' | 'DEGRADED';
    liveReachable: boolean;
    products: string[];
    wsHealth: CoinbaseWsFeedHealth;
  };
  capital: {
    status: string;
    capitalBaseUsd: number;
    activeTradingCapitalUsd: number;
    drawdownReserveUsd: number;
    lockedProfitUsd: number;
    withdrawableProfitUsd: number;
    disabledCapitalUsd: number;
    topstepIncomeAvailableUsd: number;
    allocatedFromTopstepUsd: number;
    riskView: CoinbaseRiskCapitalView;
    latestEvents: CoinbaseCapitalEvent[];
  };
  market: Array<{
    productId: string;
    freshnessVerdict: 'VALID' | 'DEGRADED' | 'STALE' | 'MISSING';
    gateAction: 'ALLOW' | 'PROBE_ONLY' | 'PAPER_ONLY' | 'BLOCK_NEW';
    markPrice?: number;
    mid?: number;
    bestBid?: number;
    bestAsk?: number;
    spreadBps?: number;
    estimatedSlippageBps?: number;
    thinBook?: boolean;
    crossedBook?: boolean;
    wideSpread?: boolean;
    reasons: string[];
  }>;
  intelligence: Array<{
    productId: string;
    tomiScore?: number;
    kazeScore?: number;
    apge?: { grade?: string; action?: string; reasons?: string[] };
    chart?: { grade?: string; action?: string; reasons?: string[] };
    flow?: { domStatus?: string; tapeStatus?: string; combinedStatus?: string; reasons?: string[] };
  }>;
  risk: {
    posture: string;
    capitalView: CoinbaseRiskCapitalView;
    openPositions: unknown[];
    dailyPnlUsd?: number;
    rolling24hPnlUsd?: number;
    drawdownUsd?: number;
    marginUsagePct?: number;
    liquidationDistancePct?: number;
    dominantRiskCap?: string;
  };
  paper: {
    openPaperPositions: unknown[];
    realizedPnlUsd: number;
    unrealizedPnlUsd: number;
    latestJournal: CoinbaseJournalEntry[];
  };
  candidates: Array<{
    id: string;
    productId: string;
    side: 'long' | 'short';
    strategyClass: string;
    candidateGrade: string;
    actionLane: string;
    eligibleForPaper: boolean;
    eligibleForLive: boolean;
    preferredEntry?: number;
    stopPrice?: number;
    target1?: number;
    rewardToRisk?: number;
    reasons: string[];
    blockers: string[];
    oneLine: string;
  }>;
  candidateEvaluations: Array<{
    at: string;
    productId: string;
    side: 'long' | 'short';
    actionLane: string;
    outcome: string;
    placed: boolean;
    paperOrderId?: string;
    protectiveStopId?: string;
    dominantBlocker?: string;
    oneLine: string;
  }>;
  intelligenceBridge: Array<{
    productId: string;
    tomiSource: string;
    kazeSource: string;
    chartSource: string;
    apgeSource: string;
    greekSource: string;
    logFibSource: string;
    apgeGrade: string;
    readyForPaperCandidate: boolean;
    readyForLiveCandidate: boolean;
    blockers: string[];
  }>;
  paperForward?: {
    mode: string;
    running: boolean;
    products: string[];
    liveReachable: boolean;
    lastCycleAt?: string;
    cyclesToday: number;
    metrics: {
      cyclesRun: number;
      candidateNominatedCount: number;
      paperOrderCount: number;
      blockedCount: number;
      paperWins: number;
      paperLosses: number;
      paperTimeStops: number;
      realizedPnlUsd: number;
      profitFactor: number;
      averageCandidateGradeRank: number;
      dominantBlockers: Record<string, number>;
    };
    recentCycles: Array<{ at: string; productId: string; outcome: string; apgeGrade?: string; actionLane?: string; blocker?: string; oneLine: string }>;
  };
  defi?: {
    fetchedAt: string;
    stale: boolean;
    staleSinceMs: number;
    pools: Array<{
      protocolId: 'aave-v3' | 'morpho-blue' | 'compound-v3';
      supplyApyPct: number;
      variableBorrowApyPct: number;
      utilizationRatePct: number;
      availableLiquidityUsd: number;
      ltv: number;
      liquidationThreshold: number;
      source: 'on-chain' | 'defillama' | 'cached';
      stale: boolean;
      fetchedAt: string;
    }>;
    bestSupplyProtocol: string | null;
    bestSupplyApyPct: number;
    bestBorrowProtocol: string | null;
    bestBorrowApyPct: number;
    allocation: {
      phase: 1 | 2 | 3 | 4;
      activeTradingUsd: number;
      reserveUsd: number;
      defiTargetUsd: number;
      defiHeadroomUsd: number;
      actionLane: string;
      borrowGates: Array<{ gate: string; pass: boolean; detail: string }>;
      bestProtocol: string | null;
      recommendedLendAmountUsd: number;
      reasoning: string[];
    };
    warnings: string[];
  };
  verdict: {
    headline: string;
    engineReady: boolean;
    marketReady: boolean;
    capitalReady: boolean;
    riskReady: boolean;
    paperReady: boolean;
    dominantBlocker?: string;
    whyNotTrade?: string;
    whyNotBigger?: string;
    nextBestAction: string;
  };
}

export async function getCoinbaseDecisionLab(): Promise<CoinbaseDecisionLabPayload> {
  const res = await api.get<{ success: boolean; data: CoinbaseDecisionLabPayload }>('/coinbase/decision-lab');
  return res.data.data;
}

export interface CoinbaseCandle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface CoinbaseBarSeries {
  timeframe: string;
  candles: CoinbaseCandle[];
}

export async function getCoinbaseBars(productId: string): Promise<CoinbaseBarSeries[]> {
  const res = await api.get<{ success: boolean; data: CoinbaseBarSeries[] }>(`/coinbase/bars/${encodeURIComponent(productId)}`);
  return res.data.data;
}

export interface CoinbaseOrderRecord {
  order_id: string;
  client_order_id?: string;
  product_id: string;
  side: 'BUY' | 'SELL';
  status: string;
  time_in_force: string;
  created_time: string;
  completion_percentage: string;
  filled_size: string;
  average_filled_price: string;
  total_value_after_fees: string;
  order_type: string;
}

export interface CoinbaseFuturesPosition {
  product_id: string;
  side: 'LONG' | 'SHORT';
  number_of_contracts: string;
  current_price: string;
  avg_entry_price: string;
  unrealized_pnl: string;
  expiry: string;
}

export interface CoinbaseFuturesBalance {
  futures_buying_power: string;
  total_usd_balance: string;
  cbi_usd_balance: string;
  cfm_usd_balance: string;
  unrealized_pnl: string;
  daily_realized_pnl: string;
  initial_margin: string;
  available_margin: string;
  liquidation_buffer_percentage: string;
}

export interface CoinbaseOrderHistoryPayload {
  orders: CoinbaseOrderRecord[];
  positions: CoinbaseFuturesPosition[];
  balance: CoinbaseFuturesBalance | null;
}

export async function getCoinbaseOrderHistory(): Promise<CoinbaseOrderHistoryPayload> {
  const res = await api.get<{ success: boolean; data: CoinbaseOrderHistoryPayload }>('/coinbase/order-history');
  return res.data.data;
}

export async function clearCoinbaseKillSwitch(reason: string): Promise<{ message: string; wasReason: string }> {
  const res = await api.post<{ success: boolean; message: string; wasReason: string }>('/coinbase/kill-switch/clear', { reason });
  return res.data;
}
